import type { Task } from '@obv/api-client';
import { EVERYONE, clockFace, eyebrowDate, groupTasks, shortWeekday, toUnit } from '@obv/core';
import { personColor } from '@obv/ui';
import { useEffect, useState } from 'react';
import { useLocation } from 'wouter';
import { newMutationKey } from '@/queries/hooks';
import { useUpdateTask } from '@/queries/chores';
import { useChoreData } from '../choreData';
import { memberLabel, useDisplay } from '../context';
import { WeatherIcon } from '../icons';
import { Card, Unavailable } from '../ui';
import { useWeatherState } from './weatherState';

// Building blocks shared by the Family dashboard scene and the Dashboard home template.

const FADE_RIGHT = 'linear-gradient(to right, #000 calc(100% - 48px), transparent)';

/**
 * Today's open chores (overdue first) as tappable pills; done ones fade to the end. `people` is the
 * "Our family" style: who it is and how they're doing (Late / To do / Done), the chore in its label.
 */
export function ChorePills({ people = false }: { people?: boolean }) {
  const ctx = useDisplay();
  const { tasks, completedOn } = useChoreData();
  const update = useUpdateTask();
  const today = ctx.clock.dateKey;
  const g = tasks.data ? groupTasks(tasks.data.items, today, EVERYONE, completedOn) : null;
  const pills: Task[] = g ? [...g.overdue, ...g.today, ...g.doneToday] : [];
  return (
    <div
      className="scroll-x flex min-w-0 flex-1 items-center"
      aria-label="Chores today"
      // Pills scroll sideways; fade them out before they reach the clock instead of cutting them off.
      style={{ WebkitMaskImage: FADE_RIGHT, maskImage: FADE_RIGHT }}
    >
      {g && pills.length === 0 ? <Pill title="All chores done" sub="Nothing left today" color="var(--green)" mark="✓" /> : null}
      {pills.map((t) => {
        const who = memberLabel(ctx, t.member_id);
        const late = !t.completed && t.due_date !== null && t.due_date < today;
        if (people) {
          return (
            <PersonPill
              key={t.id}
              name={who.name}
              initial={who.initial}
              color={personColor(who.colorKey)}
              status={t.completed ? 'done' : late ? 'late' : 'todo'}
              label={`${who.name}: ${t.title}, ${t.completed ? 'done' : late ? 'late' : 'to do'}. Tap to ${t.completed ? 'undo' : 'tick off'}.`}
              onClick={t.id.indexOf('tmp-') === 0 ? undefined : () => update.mutate({ id: t.id, body: { completed: !t.completed }, key: newMutationKey() })}
            />
          );
        }
        return (
          <Pill
            key={t.id}
            title={t.title}
            sub={`${who.name}${late ? ' · late' : t.completed ? ' · done' : ''}`}
            color={personColor(who.colorKey)}
            mark={t.completed ? '✓' : late ? '!' : who.initial}
            done={t.completed}
            label={`${t.title}${t.completed ? ', done' : ''}. Tap to ${t.completed ? 'undo' : 'tick off'}.`}
            onClick={t.id.indexOf('tmp-') === 0 ? undefined : () => update.mutate({ id: t.id, body: { completed: !t.completed }, key: newMutationKey() })}
          />
        );
      })}
    </div>
  );
}

const STATUS: Record<'late' | 'todo' | 'done', { text: string; color: string }> = {
  late: { text: 'Late', color: 'var(--now)' },
  todo: { text: 'To do', color: 'var(--sun)' },
  done: { text: 'Done', color: 'var(--green)' },
};

function PersonPill({ name, initial, color, status, label, onClick }: { name: string; initial: string; color: string; status: 'late' | 'todo' | 'done'; label: string; onClick?: () => void }) {
  const st = STATUS[status];
  return (
    <button type="button" disabled={!onClick} onClick={onClick} aria-label={label} className="tap mr-5 flex h-[76px] flex-none items-center text-left">
      <span className="flex h-[64px] w-[64px] flex-none items-center justify-center rounded-full text-[24px] font-medium" style={{ background: color, color: '#fff', opacity: 0.85 }} aria-hidden>
        {initial}
      </span>
      <span className="ml-3">
        <span className="block text-[19px] font-semibold" style={{ fontFamily: 'var(--headFont, inherit)' }}>
          {name}
        </span>
        <span className="mt-[2px] flex items-center text-[15px] text-muted2">
          <span className="mr-[6px] h-[8px] w-[8px] rounded-full" style={{ background: st.color }} />
          {st.text}
        </span>
      </span>
    </button>
  );
}

function Pill({ title, sub, color, mark, done, label, onClick }: { title: string; sub: string; color: string; mark: string; done?: boolean; label?: string; onClick?: () => void }) {
  return (
    <button
      type="button"
      disabled={!onClick}
      onClick={onClick}
      aria-label={label || title}
      className="tap card mr-3 flex h-[60px] max-w-[260px] flex-none items-center rounded-full py-2 pl-2 pr-5 text-left"
      style={{ background: 'var(--card)', opacity: done ? 0.55 : 1 }}
    >
      <span className="flex h-[44px] w-[44px] flex-none items-center justify-center rounded-full text-[16px] font-semibold" style={{ background: color, color: '#fff' }} aria-hidden>
        {mark}
      </span>
      <span className="ml-3 min-w-0">
        <span className="ellipsis block text-[15px] font-semibold" style={{ textDecoration: done ? 'line-through' : 'none' }}>
          {title}
        </span>
        <span className="ellipsis block text-[12px] text-muted">{sub}</span>
      </span>
    </button>
  );
}

/** Seconds re-render on their own so the rest of the dashboard only updates once a minute. */
function Seconds() {
  const [s, setS] = useState(() => new Date().getSeconds());
  useEffect(() => {
    const t = setInterval(() => setS(new Date().getSeconds()), 1000);
    return () => clearInterval(t);
  }, []);
  return <>{s < 10 ? `0${s}` : s}</>;
}

export function TimeBlock() {
  const ctx = useDisplay();
  const [, navigate] = useLocation();
  const face = clockFace(ctx.clock.hhmm, ctx.prefs.h24);
  return (
    <div className="ml-4 flex flex-none items-center">
      <button type="button" className="tap text-right" onClick={() => navigate('/display/clock')} aria-label="Open clock">
        <div className="flex items-start justify-end leading-none">
          <span className="tabular font-semibold tracking-[-0.03em]" style={{ fontSize: 64 }}>
            {face.main}
          </span>
          <span className="tabular ml-1 mt-1 text-[24px] font-medium text-muted">
            {face.suffix || <Seconds />}
          </span>
        </div>
        <div className="mt-1 text-[13px] text-muted">{eyebrowDate(ctx.clock.dateKey)}</div>
      </button>
    </div>
  );
}

/** Big current conditions plus the next few days as columns. */
export function WeatherHeroCard({ days }: { days: number }) {
  const ctx = useDisplay();
  const { w, pending } = useWeatherState();
  if (!w) {
    return (
      <Card tone={2}>
        <Unavailable>{pending ? 'Fetching the weather…' : 'Weather unavailable right now.'}</Unavailable>
      </Card>
    );
  }
  const T = (c: number) => toUnit(c, ctx.prefs.unit);
  const today = w.daily[0];
  const next = w.daily.slice(1, 1 + days);
  return (
    <Card tone={2} className="flex flex-col items-center justify-between overflow-hidden px-5 py-5 text-center">
      <div className="flex items-center justify-center">
        <WeatherIcon kind={w.current.kind} size={60} isDay={w.current.is_day} />
        <div className="ml-4 font-semibold leading-none tracking-[-0.03em]" style={{ fontSize: 72 }}>
          {T(w.current.temp_c)}°
        </div>
      </div>
      <div>
        <div className="text-[22px] font-semibold">{w.current.text}.</div>
        {today ? (
          <div className="mt-1 text-[14px] uppercase tracking-[0.06em] text-muted">
            High {T(today.max_c)}° · Low {T(today.min_c)}°
          </div>
        ) : null}
      </div>
      <div className="grid w-full" style={{ gridTemplateColumns: `repeat(${next.length}, minmax(0,1fr))` }}>
        {next.map((d) => (
          <div key={d.date} className="flex flex-col items-center">
            <div className="text-[13px] text-muted">{shortWeekday(d.date, ctx.clock.dateKey)}</div>
            <div className="my-1">
              <WeatherIcon kind={d.kind} size={26} />
            </div>
            <div className="text-[16px] font-semibold">{T(d.max_c)}°</div>
            <div className="text-[12px] text-muted">{T(d.min_c)}°</div>
          </div>
        ))}
      </div>
    </Card>
  );
}

/** "Our family" header: the household's name, who's doing what, and a big serif clock. */
export function FamilyTopBar({ className = '' }: { className?: string }) {
  const ctx = useDisplay();
  const [, navigate] = useLocation();
  const face = clockFace(ctx.clock.hhmm, ctx.prefs.h24);
  const serif = { fontFamily: 'var(--clockFont, inherit)' };
  // Portrait is narrow: drop the name so the people still fit.
  const compact = ctx.orientation === 'portrait';
  return (
    <div className={`relative flex h-[104px] flex-none items-center ${className}`}>
      {compact ? null : (
        <div className="mr-6 flex-none text-[34px] font-semibold leading-none tracking-[0.01em]" style={serif}>
          {ctx.boot.household.name}
        </div>
      )}
      <ChorePills people />
      <button type="button" className="tap relative z-[1] ml-4 mr-[58px] flex-none text-right" onClick={() => navigate('/display/clock')} aria-label="Open clock" style={serif}>
        <span className="flex items-baseline justify-end leading-none">
          <span className="tabular font-semibold tracking-[-0.02em]" style={{ fontSize: 80 }}>
            {face.main}
          </span>
          {face.suffix ? <span className="ml-2 text-[24px] font-semibold">{face.suffix}</span> : null}
        </span>
        <span className="mt-1 block text-[18px] text-muted2">
          {eyebrowDate(ctx.clock.dateKey)}, {ctx.boot.household.location.name.split(',')[0]}
        </span>
      </button>
      <Leaf />
    </div>
  );
}

/** A sprig in the corner, drawn in the palette's leaf colour; purely decorative. */
function Leaf() {
  return (
    <svg className="pointer-events-none absolute" style={{ right: -30, top: -26, color: 'var(--leaf, transparent)' }} width="70" height="104" viewBox="0 0 86 128" aria-hidden>
      <path d="M70 126C64 92 58 60 40 6" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
      <g fill="currentColor" opacity="0.9">
        <path d="M44 16c12-8 26-6 34 2-12 6-24 6-34-2Z" />
        <path d="M46 30C34 22 20 22 12 30c12 6 24 6 34 0Z" />
        <path d="M52 46c12-8 26-6 32 2-12 6-24 6-32-2Z" />
        <path d="M55 62C43 54 29 54 21 62c12 6 24 6 34 0Z" />
        <path d="M60 80c11-8 24-6 30 2-11 6-22 6-30-2Z" />
        <path d="M62 96c-11-7-23-6-30 1 11 6 22 5 30-1Z" />
      </g>
    </svg>
  );
}
